import React, { useState, useEffect } from "react";
import Sidebar from "./Sidebar";
import { FaBarsStaggered } from "react-icons/fa6";
import { IoSearchOutline } from "react-icons/io5";
import Loader from "../../components/Loader";
import AdminNav from "./AdminNav";
import defaultAxios from "../../utils/axios";
import axios from "axios";
import { format } from "date-fns";
import { DatePicker, Pagination } from "antd";
import dayjs from "dayjs"; // for comparing months

const Profit = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profits, setProfits] = useState([]);
  const [filteredProfits, setFilteredProfits] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // New states:
  const [newProfit, setNewProfit] = useState("");
  const [type, setType] = useState("profit");
  const [funds, setFunds] = useState([]);
  const [fundId, setFundId] = useState("");
  const [fundClasses, setFundClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [monthFilter, setMonthFilter] = useState(null);
  const [rangeFilter, setRangeFilter] = useState(null); // [startDate, endDate]

  const pageSize = 10;
  const token = localStorage.getItem("admintoken");
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  // Fetch profits
  useEffect(() => {
    const fetchProfits = async () => {
      setLoading(true);
      try {
        const response = await defaultAxios.get("/api/v1/admin/profit", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const sortedData = response.data.result.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        setProfits(sortedData);
        setFilteredProfits(sortedData);
      } catch (error) {
        console.error("Error fetching profits:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfits();
  }, [token]);

  useEffect(() => {
    const fetchFunds = async () => {
      setLoading(true);
      try {
        const { data } = await defaultAxios.get(
          `${process.env.REACT_APP_API}/api/v1/fund`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setFunds(data);
      } catch (error) {
        console.error("Error fetching funds:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchFunds();
  }, [token]);

  useEffect(() => {
    if (!fundId) {
      setFundClasses([]);
      return;
    }

    const fetchfundClasses = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API}/api/v1/fund/classes/${fundId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setFundClasses(data.data);
      } catch (error) {
        console.error("Error fetching fund classes:", error);
        setFundClasses([]);
      } finally {
        setLoading(false);
      }
    };

    fetchfundClasses();
  }, [fundId, token]);

  // Combined Filtering
  useEffect(() => {
    let filtered = profits;

    // Date search
    if (searchTerm.trim()) {
      filtered = filtered.filter(
        (profit) =>
          format(new Date(profit.createdAt), "yyyy-MM-dd") === searchTerm
      );
    }

    // Type filter
    if (typeFilter !== "all") {
      filtered = filtered.filter((profit) => profit.type === typeFilter);
    }

    // Month filter
    if (monthFilter) {
      filtered = filtered.filter((profit) =>
        dayjs(profit.createdAt).isSame(monthFilter, "month")
      );
    }

    // Range filter
    if (rangeFilter && rangeFilter.length === 2) {
      const [start, end] = rangeFilter;
      filtered = filtered.filter((profit) => {
        const created = dayjs(profit.createdAt);
        return (
          created.isAfter(start.subtract(1, "day")) &&
          created.isBefore(end.add(1, "day"))
        );
      });
    }

    setFilteredProfits(filtered);
    setCurrentPage(1);
  }, [searchTerm, typeFilter, monthFilter, rangeFilter, profits]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await defaultAxios.post(
        "/api/v1/admin/add-profit",
        {
          amount: newProfit,
          fundId,
          classId,
          type,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setProfits((prev) => [res.data.result, ...prev]);
      setFilteredProfits((prev) => [res.data.result, ...prev]);
      setNewProfit("");
      setType("profit");
      setFundId("");
      setClassId("");
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error adding profit:", error);
    } finally {
      setLoading(false);
    }
  };

  const paginatedData = filteredProfits.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return (
    <div className="wrapper d-flex align-items-stretch">
      {loading && <Loader />}
      <Sidebar sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <div id="content" className="px-2">
        <button
          type="button"
          id="openSidebar"
          onClick={toggleSidebar}
          className="bars-btn"
        >
          <FaBarsStaggered />
        </button>

        <AdminNav />
        <h3 className="b-clr mb-4">Daily Profit</h3>

        <div className="search-add-main">
          <div className="d-flex flex-wrap gap-3 align-items-end">
            {/* Date Picker */}
            <div className="search-main mt-0">
              <input
                type="date"
                className="search-inp"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="search-icon">
                <IoSearchOutline />
              </div>
            </div>

            {/* Type Filter */}
            <div className="other-filter-sub">
              <label className="bc-clr">Type</label>
              <select
                className="filter-inp"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="all">All</option>
                <option value="profit">Profit</option>
                <option value="loss">Loss</option>
              </select>
            </div>

            {/* Month Picker */}
            <div className="other-filter-sub">
              <label className="bc-clr">Month</label>
              <DatePicker
                picker="month"
                onChange={(date) => setMonthFilter(date)}
                allowClear
              />
            </div>

            {/* Date Range Picker */}
            <div className="other-filter-sub">
              <label className="bc-clr">Date Range</label>
              <DatePicker.RangePicker
                onChange={(range) => setRangeFilter(range)}
                allowClear
              />
            </div>
          </div>

          <button
            className="btn btn-primary me-3"
            onClick={() => setIsModalOpen(true)}
          >
            Add Profit
          </button>
        </div>

        <div className="tbl-main">
          <table className="simple-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Amount</th>
                <th>Type</th>
                <th>Fund</th>
                <th>Class</th>
                <th>Currency</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData?.length > 0 ? (
                paginatedData.map((profit) => (
                  <tr key={profit._id}>
                    <td>{format(new Date(profit.createdAt), "dd MMM yyyy")}</td>
                    <td
                      style={{
                        color: profit.type === "profit" ? "green" : "red",
                      }}
                    >
                      {profit.amount.toFixed(2)}
                    </td>
                    <td style={{ textTransform: "capitalize" }}>
                      {profit.type}
                    </td>
                    <td>{profit.fund?.name || "—"}</td>
                    <td>{profit.class?.class || "—"}</td>
                    <td>{profit.class?.currency || "—"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6">No profit records found</td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="mt-3 d-flex justify-content-end">
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={filteredProfits.length}
              onChange={(page) => setCurrentPage(page)}
              showSizeChanger={false}
            />
          </div>
        </div>

        {/* Modal remains the same */}
        {isModalOpen && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Add Profit</h3>
              <form onSubmit={handleSubmit}>
                <div className="form-group mb-3">
                  <label className="d-flex justify-content-start">Type</label>
                  <select
                    className="form-control"
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                  >
                    <option value="profit">Profit</option>
                    <option value="loss">Loss</option>
                  </select>
                </div>

                <div className="form-group mb-3">
                  <label className="d-flex justify-content-start">Fund</label>
                  <select
                    className="form-control"
                    value={fundId}
                    onChange={(e) => {
                      setFundId(e.target.value);
                      setClassId("");
                    }}
                    required
                  >
                    <option value="">Select Fund</option>
                    {funds.map((fund) => (
                      <option key={fund._id} value={fund._id}>
                        {fund.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group mb-3">
                  <label className="d-flex justify-content-start">Class</label>
                  <select
                    className="form-control"
                    value={classId}
                    onChange={(e) => setClassId(e.target.value)}
                    disabled={!fundClasses.length}
                    required
                  >
                    <option value="">
                      {fundClasses.length
                        ? "Select Class"
                        : "No classes available"}
                    </option>
                    {fundClasses.map((cls) => (
                      <option key={cls._id} value={cls._id}>
                        {cls.class}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group mb-3">
                  <label className="d-flex justify-content-start">Amount</label>
                  <input
                    type="number"
                    name="amount"
                    className="form-control"
                    value={newProfit}
                    onChange={(e) => setNewProfit(e.target.value)}
                    required
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className="d-flex justify-content-start gap-3">
                  <button type="submit" className="btn btn-primary">
                    Add Profit
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setIsModalOpen(false);
                      setFundId("");
                      setClassId("");
                      setNewProfit("");
                      setType("profit");
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profit;
